import { Link } from 'react-router-dom';
import SpotHeroVideo from '@/components/SpotHeroVideo';
import { Button } from '@/components/ui/button';
import { Sprout, Map as MapIcon, Leaf, Sparkles, ArrowRight, MessageSquare, ShieldCheck } from 'lucide-react';

const Stat = ({ n, label }) => (
  <div>
    <div className="font-display text-5xl md:text-6xl tracking-tighter text-chartreuse">{n}</div>
    <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-1">{label}</div>
  </div>
);

const Step = ({ n, title, body, icon: I }) => (
  <div className="flex gap-5">
    <div className="shrink-0 w-12 h-12 rounded-2xl bg-secondary grid place-items-center">
      <I size={20} strokeWidth={1.6}/>
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-terracotta font-bold">Passo {n}</div>
      <h3 className="font-display text-2xl tracking-tight mt-0.5">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
    </div>
  </div>
);

export default function Promo() {
  return (
    <div>
      {/* HERO CINEMATOGRAFICO */}
      <section className="relative bg-obsidian text-white">
        <div className="absolute inset-0 opacity-90">
          <SpotHeroVideo aspectClass="h-[92vh] md:h-[100vh]" overlayGradient={false}/>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/40 via-obsidian/20 to-obsidian pointer-events-none"/>

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 h-[92vh] md:h-[100vh] flex flex-col justify-end pb-16 md:pb-24">
          <div className="text-[11px] uppercase tracking-[0.4em] text-chartreuse font-bold mb-4">
            Ecosistema AgriTech · Italia
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter">
            La terra<br/>incontra le persone.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/70 max-w-xl leading-relaxed">
            C'è chi cerca un pezzo di terra italiana da coltivare.
            C'è chi ha una terra italiana da tramandare.
            Prima si incontravano per caso. Adesso su AgriNexus.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/esplora"><Button size="lg" className="rounded-full h-12 px-7 bg-chartreuse text-obsidian hover:bg-chartreuse/90">Trova un terreno <ArrowRight size={16} className="ml-2"/></Button></Link>
            <Link to="/pubblica"><Button size="lg" variant="outline" className="rounded-full h-12 px-7 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">Pubblica un terreno</Button></Link>
          </div>
        </div>
      </section>

      {/* IL PROBLEMA */}
      <section className="bg-obsidian text-white py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-terracotta font-bold mb-4">Il problema</div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-[1.05]">
            Ogni anno migliaia di ettari agricoli restano abbandonati.<br/>
            <span className="text-white/50">Non per mancanza di interesse, ma di connessioni.</span>
          </h2>
          <div className="mt-14 grid md:grid-cols-3 gap-10">
            <Stat n="12M" label="ha coltivabili in Italia"/>
            <Stat n="3M" label="ha in stato di abbandono"/>
            <Stat n="1" label="piattaforma per ripartire"/>
          </div>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="py-24 md:py-32 bg-background">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-terracotta font-bold mb-4">Come funziona</div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-[1.05] mb-16">
            Quattro passi per<br/>far incontrare le persone giuste.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-14">
            <Step n="1" icon={MapIcon} title="Geolocalizza il tuo terreno" body="Mappa OSM interattiva, indirizzo o coordinate GPS. Il compratore vede subito dov'è."/>
            <Step n="2" icon={Sparkles} title="Pubblica in un minuto" body="Wizard guidato, foto multiple, descrizione generata dall'AI se non hai tempo di scriverla."/>
            <Step n="3" icon={MessageSquare} title="Ricevi contatti mirati" body="Messaggistica interna in tempo reale, con foto allegate. Nessun numero esposto pubblicamente."/>
            <Step n="4" icon={Leaf} title="Vendi anche i raccolti" body="Marketplace Partite: uva, olive, mele — venduti al quintale ai buyer giusti tramite filtri intelligenti."/>
          </div>
        </div>
      </section>

      {/* DIFFERENZIATORE AI */}
      <section className="py-24 md:py-32 bg-secondary">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta font-bold mb-4 flex items-center gap-2">
              <Sparkles size={14} className="text-chartreuse"/> Intelligenza Artificiale integrata
            </div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tighter leading-[1.05]">
              Un assistente agricolo<br/>a disposizione 24/7.
            </h2>
            <ul className="mt-8 space-y-4 text-sm text-muted-foreground">
              <li className="flex items-start gap-3"><ShieldCheck size={16} className="mt-0.5 shrink-0 text-chartreuse"/> <span><b className="text-foreground">Analisi terreno</b>: potenziale colturale, PSR e adempimenti — con disclaimer onesti.</span></li>
              <li className="flex items-start gap-3"><ShieldCheck size={16} className="mt-0.5 shrink-0 text-chartreuse"/> <span><b className="text-foreground">Match spiegato</b>: perché un terreno o una partita si adatta alle tue preferenze.</span></li>
              <li className="flex items-start gap-3"><ShieldCheck size={16} className="mt-0.5 shrink-0 text-chartreuse"/> <span><b className="text-foreground">Analisi foto</b>: carica una foto del terreno, l'AI descrive cosa vede.</span></li>
            </ul>
            <Link to="/assistente" className="mt-8 inline-flex"><Button className="rounded-full h-11 px-6">Prova l'assistente <ArrowRight size={14} className="ml-2"/></Button></Link>
          </div>
          <div className="rounded-3xl overflow-hidden border border-border shadow-2xl">
            <img src="/og-image.png" alt="AgriNexus AI" className="w-full aspect-[4/3] object-cover"/>
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="py-24 md:py-32 bg-obsidian text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-chartreuse font-bold mb-4">Comincia oggi</div>
          <h2 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95]">
            La tua terra<br/>ha già chi la cerca.
          </h2>
          <p className="mt-6 text-white/60 text-lg max-w-xl mx-auto">
            Registrati gratis, pubblica il tuo primo annuncio o la tua prima partita in meno di 3 minuti.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/registrati"><Button size="lg" className="rounded-full h-12 px-8 bg-chartreuse text-obsidian hover:bg-chartreuse/90">Crea account gratis</Button></Link>
            <Link to="/esplora"><Button size="lg" variant="outline" className="rounded-full h-12 px-8 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">Esplora prima</Button></Link>
          </div>
          <div className="mt-14 text-[10px] uppercase tracking-[0.3em] text-white/40 flex items-center justify-center gap-2">
            <Sprout size={12}/> AgriNexus · La terra incontra le persone
          </div>
        </div>
      </section>
    </div>
  );
}
